import { useState, useMemo } from 'react';
import { Plus, Edit3, Trash2, ChevronUp, ChevronDown, ToggleLeft, ToggleRight, Search, Layers, FolderOpen } from 'lucide-react';
import { useCatalogStore, genId } from '../store/catalogStore';
import { toast } from '../components/Toast';
import type { Category } from '../types/catalog';

const emptyCategory = (): Omit<Category, 'id' | 'createdAt' | 'updatedAt'> => ({
  name: '',
  slug: '',
  description: '',
  parentId: undefined,
  icon: '',
  isActive: true,
  displayOrder: 1,
});

const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export default function CategoriesPage() {
  const { categories, addCategory, updateCategory, deleteCategory, reorderCategory } = useCatalogStore();
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'top' | 'sub'>('top');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyCategory());

  const topCategories = useMemo(
    () => categories.filter((c) => !c.parentId).sort((a, b) => a.displayOrder - b.displayOrder),
    [categories]
  );

  const subCategories = useMemo(
    () => categories.filter((c) => !!c.parentId).sort((a, b) => a.displayOrder - b.displayOrder),
    [categories]
  );

  const displayed = useMemo(() => {
    const base = activeTab === 'top' ? topCategories : subCategories;
    return base.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));
  }, [activeTab, topCategories, subCategories, search]);

  const openAdd = () => {
    setEditingId(null);
    const isTop = activeTab === 'top';
    setForm({ ...emptyCategory(), displayOrder: (isTop ? topCategories : subCategories).length + 1, parentId: isTop ? undefined : (topCategories[0]?.id ?? undefined) });
    setShowForm(true);
  };

  const openEdit = (cat: Category) => {
    setEditingId(cat.id);
    setForm({
      name: cat.name,
      slug: cat.slug,
      description: cat.description ?? '',
      parentId: cat.parentId,
      icon: cat.icon ?? '',
      isActive: cat.isActive,
      displayOrder: cat.displayOrder,
    });
    setShowForm(true);
  };

  const handleSave = () => {
    if (!form.name.trim()) { toast.error('Validation', 'Category name is required.'); return; }
    const slug = form.slug.trim() || slugify(form.name);
    const now = new Date().toISOString();
    if (editingId) {
      updateCategory(editingId, { ...form, slug, parentId: form.parentId || undefined });
      toast.success('Category Updated', `"${form.name}" updated.`);
    } else {
      addCategory({ id: genId(), ...form, slug, parentId: form.parentId || undefined, createdAt: now, updatedAt: now });
      toast.success('Category Added', `"${form.name}" added.`);
    }
    setShowForm(false);
  };

  const handleDelete = (cat: Category) => {
    if (!confirm(`Delete category "${cat.name}"? All subcategories will also be affected.`)) return;
    deleteCategory(cat.id);
    toast.success('Category Deleted', `"${cat.name}" removed.`);
  };

  const getParentName = (parentId?: string) => {
    if (!parentId) return '—';
    return categories.find((c) => c.id === parentId)?.name ?? '—';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-[24px] font-semibold text-gray-900 tracking-tight">Categories</h1>
          <p className="text-[13px] text-gray-400 mt-0.5">
            {topCategories.length} main categories · {subCategories.length} subcategories
          </p>
        </div>
        <button
          onClick={openAdd}
          className="h-10 px-5 rounded-xl bg-gray-900 text-white text-[13px] font-medium hover:bg-gray-800 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add Category
        </button>
      </div>

      {/* Tabs + Search */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex bg-white border border-gray-200/60 rounded-xl overflow-hidden">
          <button
            onClick={() => setActiveTab('top')}
            className={`px-4 py-2 text-[13px] font-medium transition-colors ${activeTab === 'top' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
          >
            <Layers className="w-4 h-4 inline mr-1.5" />Main Categories
          </button>
          <button
            onClick={() => setActiveTab('sub')}
            className={`px-4 py-2 text-[13px] font-medium transition-colors ${activeTab === 'sub' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
          >
            <FolderOpen className="w-4 h-4 inline mr-1.5" />Subcategories
          </button>
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search categories..."
            className="w-full h-10 pl-10 pr-4 rounded-xl bg-white border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-200/60 overflow-hidden">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              <th className="px-4 py-3 text-left font-medium text-gray-500">Order</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Name</th>
              {activeTab === 'sub' && <th className="px-4 py-3 text-left font-medium text-gray-500 hidden md:table-cell">Parent</th>}
              <th className="px-4 py-3 text-left font-medium text-gray-500 hidden lg:table-cell">Slug</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Status</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayed.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                  <FolderOpen className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>No categories found</p>
                </td>
              </tr>
            )}
            {displayed.map((cat) => (
              <tr key={cat.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/30 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex gap-1 items-center">
                    <button onClick={() => reorderCategory(cat.id, 'up')} className="w-6 h-6 rounded hover:bg-gray-100 flex items-center justify-center" aria-label="Move up">
                      <ChevronUp className="w-3.5 h-3.5 text-gray-400" />
                    </button>
                    <button onClick={() => reorderCategory(cat.id, 'down')} className="w-6 h-6 rounded hover:bg-gray-100 flex items-center justify-center" aria-label="Move down">
                      <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                    </button>
                    <span className="text-gray-400 ml-1">{cat.displayOrder}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{cat.name}</p>
                  {cat.description && <p className="text-gray-400 text-[11px] truncate max-w-[200px]">{cat.description}</p>}
                </td>
                {activeTab === 'sub' && (
                  <td className="px-4 py-3 hidden md:table-cell text-gray-500">{getParentName(cat.parentId)}</td>
                )}
                <td className="px-4 py-3 hidden lg:table-cell text-gray-400">{cat.slug}</td>
                <td className="px-4 py-3">
                  <button onClick={() => updateCategory(cat.id, { isActive: !cat.isActive })} aria-label="Toggle status">
                    {cat.isActive ? (
                      <span className="flex items-center gap-1.5 text-emerald-600"><ToggleRight className="w-5 h-5" /> Active</span>
                    ) : (
                      <span className="flex items-center gap-1.5 text-gray-400"><ToggleLeft className="w-5 h-5" /> Inactive</span>
                    )}
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(cat)} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center" aria-label="Edit">
                      <Edit3 className="w-4 h-4 text-gray-500" />
                    </button>
                    <button onClick={() => handleDelete(cat)} className="w-8 h-8 rounded-lg hover:bg-red-50 flex items-center justify-center" aria-label="Delete">
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-[17px] font-semibold text-gray-900">
                {editingId ? 'Edit Category' : 'Add Category'}
              </h2>
            </div>
            <div className="px-6 py-5 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-[12px] font-medium text-gray-500 mb-1">Category Name *</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value, slug: slugify(e.target.value) }))}
                  placeholder="e.g. IP Cameras"
                  className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5"
                />
              </div>
              {/* Slug */}
              <div>
                <label className="block text-[12px] font-medium text-gray-500 mb-1">Slug</label>
                <input
                  value={form.slug}
                  onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                  placeholder="auto-generated from name"
                  className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5"
                />
              </div>
              {/* Parent Category */}
              <div>
                <label className="block text-[12px] font-medium text-gray-500 mb-1">Parent Category</label>
                <select
                  value={form.parentId ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, parentId: e.target.value || undefined }))}
                  className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5 bg-white"
                >
                  <option value="">— Top-level Category —</option>
                  {topCategories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              {/* Description */}
              <div>
                <label className="block text-[12px] font-medium text-gray-500 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-[13px] resize-none focus:outline-none focus:ring-2 focus:ring-gray-900/5"
                />
              </div>
              {/* Order & Active */}
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-[12px] font-medium text-gray-500 mb-1">Display Order</label>
                  <input
                    type="number"
                    value={form.displayOrder}
                    onChange={(e) => setForm((f) => ({ ...f, displayOrder: +e.target.value }))}
                    className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none"
                  />
                </div>
                <div className="flex items-end pb-0.5">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} className="w-4 h-4 accent-[#7FA86A]" />
                    <span className="text-[13px] text-gray-600">Active</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button onClick={() => setShowForm(false)} className="h-10 px-5 rounded-xl bg-gray-100 text-gray-600 text-[13px] font-medium hover:bg-gray-200">Cancel</button>
              <button onClick={handleSave} className="h-10 px-5 rounded-xl bg-[#7FA86A] text-white text-[13px] font-medium hover:bg-[#658C53]">
                {editingId ? 'Save Changes' : 'Add Category'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
